import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import { LumiMascot } from "../../components/Brand";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import {
  Camera, MessageCircle, Calculator, BookOpen, PenTool, FlaskConical,
  Globe2, Languages, Gamepad2, Award, BarChart3, ClipboardList,
  Accessibility, Play, Flame, Star, Sparkles, ArrowRight,
} from "lucide-react";
import { applyAccessibility } from "../AccessibilitySettings";

export default function StudentDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const nav = useNavigate();

  const TILES = [
    { to: "/app/student/scan",      key: "scan", label: t("student.tiles.scan"), icon: Camera, bg: "bg-blue-500", text: "text-white" },
    { to: "/app/student/ask",       key: "ask",  label: t("student.tiles.ask"), icon: MessageCircle, bg: "bg-green-500", text: "text-white" },
    { to: "/app/student/ask?subject=math",           key: "math",       label: t("student.tiles.math"),       icon: Calculator, bg: "bg-yellow-400", text: "text-slate-900" },
    { to: "/app/student/ask?subject=reading",        key: "reading",    label: t("student.tiles.reading"),    icon: BookOpen,   bg: "bg-orange-400", text: "text-white" },
    { to: "/app/student/ask?subject=writing",        key: "writing",    label: t("student.tiles.writing"),    icon: PenTool,    bg: "bg-pink-400",   text: "text-white" },
    { to: "/app/student/ask?subject=science",        key: "science",    label: t("student.tiles.science"),    icon: FlaskConical, bg: "bg-teal-500", text: "text-white" },
    { to: "/app/student/ask?subject=social_studies", key: "social",     label: t("student.tiles.social_studies"), icon: Globe2, bg: "bg-indigo-500", text: "text-white" },
    { to: "/app/student/ask?subject=language",       key: "lang",       label: t("student.tiles.language"),  icon: Languages,  bg: "bg-purple-500", text: "text-white" },
    { to: "/app/student/ask?mode=quiz",              key: "games",      label: t("student.tiles.games"),icon: Gamepad2,   bg: "bg-slate-800", text: "text-white" },
    { to: "/app/student/assignments",                key: "asg",        label: t("student.tiles.assignments"),bg:"bg-blue-700", icon: ClipboardList, text: "text-white" },
    { to: "/app/student/progress",                   key: "prog",       label: t("student.tiles.progress"),icon: BarChart3,  bg: "bg-emerald-600", text: "text-white" },
    { to: "/app/student/rewards",                    key: "rew",        label: t("student.tiles.rewards"),    icon: Award,      bg: "bg-yellow-500", text: "text-slate-900" },
  ];

  useEffect(() => {
    applyAccessibility(user?.profile?.accessibility);
    api
      .get("/student/dashboard")
      .then((r) => setData(r.data))
      .catch((err) => console.warn("Student dashboard load failed:", err));
  }, [user]);

  const displayName = user?.profile?.display_name || user?.full_name?.split(" ")[0] || "friend";

  return (
    <AppShell isStudent>
      {/* Hero row */}
      <section className="grid lg:grid-cols-12 gap-6 items-stretch">
        <div className="lg:col-span-8 rounded-[28px] bg-white border border-slate-200 hb-chunky-shadow p-6 sm:p-8 flex items-center gap-6">
          <div className="hidden sm:block shrink-0"><LumiMascot size={140} className="lumi-bounce" /></div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("student.welcomeBack")}</div>
            <h1 className="font-student font-bold text-3xl sm:text-4xl text-slate-900">{t("student.hi", { name: displayName })}</h1>
            <p className="text-slate-700 mt-1">{t("student.ready")}</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <button data-testid="continue-last-lesson"
                      onClick={() => nav("/app/student/ask")}
                      className="rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-slate-800">
                <Play className="w-4 h-4" /> {t("student.continueLast")}
              </button>
              <Link to="/app/student/scan"
                    data-testid="quick-scan"
                    className="rounded-full bg-blue-500 text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-blue-600">
                <Camera className="w-4 h-4" /> {t("student.quickScan")}
              </Link>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 grid grid-cols-2 gap-3">
          <StatCard label={t("student.streak")} value={`${data?.stats?.streak_days ?? 0} ${t("student.days")}`} icon={Flame} color="bg-orange-500" testid="stat-streak" />
          <StatCard label={t("student.stars")} value={data?.stats?.stars ?? 0} icon={Star} color="bg-yellow-500" testid="stat-stars" />
          <StatCard label={t("student.scans")} value={data?.stats?.scans ?? 0} icon={Camera} color="bg-blue-500" testid="stat-scans" />
          <StatCard label={t("student.sessions")} value={data?.stats?.tutor_turns ?? 0} icon={Sparkles} color="bg-green-500" testid="stat-sessions" />
        </div>
      </section>

      {/* Subject / feature tiles */}
      <section className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-student font-bold text-2xl text-slate-900">{t("student.chooseSubject")}</h2>
          <Link to="/app/accessibility" className="text-sm text-blue-700 flex items-center gap-1"><Accessibility className="w-4 h-4"/> {t("nav.accessibility")}</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {TILES.map((tile) => (
            <Link key={tile.key} to={tile.to} data-testid={`tile-${tile.key}`}
                  className={`hb-tile ${tile.bg} ${tile.text} hb-chunky-shadow hb-btn-press`}>
              <tile.icon className="w-8 h-8 mb-3 opacity-90" />
              <div className="font-student font-bold text-lg">{tile.label}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Assignments + badges */}
      <section className="mt-10 grid md:grid-cols-3 gap-4">
        <div className="hb-card p-6 md:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-adult font-bold text-slate-900 text-xl">{t("student.assignmentsDue")}</h3>
            <Link to="/app/student/assignments" className="text-sm text-blue-700 flex items-center gap-1">{t("student.seeAll")} <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="mt-3 space-y-2" data-testid="assignments-list">
            {(!data?.assignments || data.assignments.length === 0) && (
              <EmptyState message={t("student.noAssignments")} />
            )}
            {data?.assignments?.map((a) => (
              <div key={a.id} className="p-3 rounded-xl border border-slate-200 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{a.title}</div>
                  <div className="text-xs text-slate-500">{a.subject} · {t("student.grade")} {a.grade_level}{a.due_date ? ` · ${new Date(a.due_date).toLocaleDateString()}` : ""}</div>
                </div>
                <Link to="/app/student/assignments"><button className="text-blue-700 text-sm font-semibold">{t("common.open")}</button></Link>
              </div>
            ))}
          </div>
        </div>
        <div className="hb-card p-6">
          <h3 className="font-adult font-bold text-slate-900 text-xl">{t("student.badges")}</h3>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="badges-list">
            {(!data?.badges || data.badges.length === 0) && <EmptyState small message={t("student.earnFirstBadge")} />}
            {data?.badges?.map((b) => (
              <div key={b.key} className="px-3 py-1.5 rounded-full bg-yellow-100 border border-yellow-200 text-yellow-900 text-xs font-semibold">
                {b.name}
              </div>
            ))}
          </div>
        </div>
      </section>
    </AppShell>
  );
}

function StatCard({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="hb-card p-4" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${color} text-white flex items-center justify-center`}><Icon className="w-4 h-4" /></div>
      <div className="mt-2 font-student font-bold text-2xl text-slate-900">{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    </div>
  );
}

function EmptyState({ message, small }) {
  return (
    <div className={`flex items-center gap-3 p-3 ${small ? "" : "border border-dashed border-slate-200 rounded-xl"}`}>
      <LumiMascot size={40} />
      <div className="text-sm text-slate-600">{message}</div>
    </div>
  );
}
